import React, { useState } from "react";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreHoriz as MoreHorizIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Portal,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import Dialog from "@/components/Dialog";
import Padding from "@/components/layout/Padding";
import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()({
  icon: {
    color: "#010101",
    marginRight: 8,
    fontSize: 12,
  },
  text: {
    fontSize: 12,
    fontWeight: 600,
    color: "#949BA8",
  },
  menuItem: {
    marginTop: 4,
  },
  deleteDialogPaper: {
    minWidth: 400,
    maxWidth: 400,
  },
  deleteDialogContent: {
    paddingTop: 0,
  },
  deleteDialogIcon: {
    maxHeight: 60,
    fontSize: 60,
    color: colors.secondary,
  },
  deleteDialogTitle: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: colors.dark,
    textAlign: "center",
  },
  deleteDialogMessage: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "22px",
    color: colors.gray,
    textAlign: "center",
  },
  deleteDialogButton: {
    minWidth: 125,
  },
  itemIcon: {
    minWidth: 30,
  },
});

type OptionMenuProps = {
  onEdit: () => void;
  onDelete: () => void;
};

const OptionMenu: React.FC<OptionMenuProps> = ({ onEdit, onDelete }) => {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const deleteDialog = useDialogState();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    onDelete();
    _closeDeleteDialog();
  };

  const _handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const _handleClose = () => {
    setAnchorEl(null);
  };

  const _handleEdit = () => {
    onEdit();
    _handleClose();
  };

  const _openDeleteDialog = () => {
    deleteDialog.show();
    _handleClose();
  };

  const _closeDeleteDialog = () => {
    setIsDeleting(false);
    deleteDialog.hide();
  };

  return (
    <Box display="flex" flexDirection="row" marginLeft="auto">
      <IconButton onClick={_handleOpen} size="large">
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={_handleClose}
      >
        <MenuItem className={classes.menuItem} onClick={_handleEdit}>
          <EditIcon className={classes.icon} />
          <Typography
            className={classes.text}
            variant="body2"
            color="textPrimary"
          >
            Edit
          </Typography>
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={_openDeleteDialog}>
          <DeleteIcon className={classes.icon} style={{ color: "#FF6262" }} />
          <Typography
            className={classes.text}
            variant="body2"
            style={{ color: "#FF6262" }}
          >
            Delete
          </Typography>
        </MenuItem>
      </Menu>
      <Portal>
        <Dialog
          title={"Delete past log"}
          open={deleteDialog.shown}
          onClose={deleteDialog.hide}
          classes={{ paper: classes.deleteDialogPaper }}
          contentClassName={classes.deleteDialogContent}
          content={
            <Box display="flex" margin="8px" alignItems="center">
              <Box display="flex" margin={9} alignItems="center">
                <div className={classes.deleteDialogIcon}>
                  <WarningIcon fontSize="inherit" color="inherit" />
                </div>
                <Typography className={classes.deleteDialogTitle}>
                  Are you sure?
                </Typography>
              </Box>
              <Padding horizontal={4}>
                <Typography className={classes.deleteDialogMessage}>
                  Are you sure you want to delete this past log?
                </Typography>
              </Padding>
            </Box>
          }
          actions={
            <Button
              size="large"
              className={classes.deleteDialogButton}
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size="1.6rem" /> : <>Confirm</>}
            </Button>
          }
        />
      </Portal>
    </Box>
  );
};

export default OptionMenu;
